import type { OpenerDescalceRow } from '../../api/useOpenerDescalce';
import { residualIsReal } from './descalceResidual';

/**
 * PURE comparator for the Descalce screen table. No React here — see
 * `descalceSort.test.ts` for the mutation-tested guards.
 *
 * Ranking rule for the `descalce` column. Deliberately NO LONGER a mirror of
 * `live/venuePositions.ts` → `venueSortMagnitude`, which still ranks every
 * resolving row at `0`; this screen has the `resolvingVenue` the opener page
 * does not:
 *   - `venue === null`, `ambiguous`, or `marketsIncomplete` → `Infinity`. In
 *     none of the three is the number a real cross-venue descalce, and an
 *     unknown must lead rather than sink into the flat majority.
 *   - a resolving row whose residual is NOT trustworthy (see `residualIsReal`)
 *     → `0`, the old artefact handling.
 *   - otherwise → `venue.descalce` (already `|signedNet|`). A resolving row
 *     with a trustworthy residual ranks by magnitude like any naked leg:
 *     burying it would break the screen's "largest naked leg first" promise.
 * Ties break A→Z on `pairName`.
 */
export function descalceRankMagnitude(row: OpenerDescalceRow): number {
  if (
    row.venue == null ||
    row.venue.ambiguous === true ||
    row.venue.marketsIncomplete === true
  ) {
    return Number.POSITIVE_INFINITY;
  }
  if (row.venue.resolving === true && !residualIsReal(row.venue)) return 0;
  return Number.isFinite(row.venue.descalce)
    ? row.venue.descalce
    : Number.POSITIVE_INFINITY;
}

export type DescalceSortColumn =
  | 'descalce'
  | 'pairName'
  | 'group'
  | 'polyYes'
  | 'polyNo'
  | 'kalshiYes'
  | 'kalshiNo'
  | 'belief'
  | 'divergence'
  | 'ruleStatus';

export type DescalceSortDirection = 'asc' | 'desc';

export interface DescalceSortState {
  column: DescalceSortColumn;
  direction: DescalceSortDirection;
}

export const DEFAULT_DESCALCE_SORT: DescalceSortState = {
  column: 'descalce',
  direction: 'desc'
};

/** Value used to sort a non-`descalce` column; `null` always sorts last. */
function secondaryColumnValue(
  row: OpenerDescalceRow,
  column: DescalceSortColumn
): number | string | null {
  switch (column) {
    case 'pairName':
      return (row.pairName ?? '').toLowerCase();
    case 'group':
      return (row.group.name ?? 'ungrouped').toLowerCase();
    case 'polyYes':
      return row.venue?.polyYes ?? null;
    case 'polyNo':
      return row.venue?.polyNo ?? null;
    case 'kalshiYes':
      return row.venue?.kalshiYes ?? null;
    case 'kalshiNo':
      return row.venue?.kalshiNo ?? null;
    case 'belief':
      return row.belief?.descalce ?? null;
    case 'divergence':
      return row.divergence != null ? Math.abs(row.divergence.total) : null;
    case 'ruleStatus':
      return (row.ruleStatus ?? '').toLowerCase();
    default:
      return null;
  }
}

const pairNameAsc = (a: OpenerDescalceRow, b: OpenerDescalceRow): number =>
  (a.pairName ?? '').localeCompare(b.pairName ?? '');

export function compareDescalceRows(
  a: OpenerDescalceRow,
  b: OpenerDescalceRow,
  sort: DescalceSortState = DEFAULT_DESCALCE_SORT
): number {
  if (sort.column === 'descalce') {
    const magA = descalceRankMagnitude(a);
    const magB = descalceRankMagnitude(b);
    if (magA !== magB) {
      return sort.direction === 'desc' ? magB - magA : magA - magB;
    }
    return pairNameAsc(a, b);
  }

  const va = secondaryColumnValue(a, sort.column);
  const vb = secondaryColumnValue(b, sort.column);
  if (va == null && vb == null) return pairNameAsc(a, b);
  if (va == null) return 1;
  if (vb == null) return -1;
  if (va !== vb) {
    if (typeof va === 'number' && typeof vb === 'number') {
      return sort.direction === 'desc' ? vb - va : va - vb;
    }
    const cmp = String(va).localeCompare(String(vb));
    return sort.direction === 'desc' ? -cmp : cmp;
  }
  return pairNameAsc(a, b);
}

export function sortDescalceRows(
  rows: OpenerDescalceRow[],
  sort: DescalceSortState = DEFAULT_DESCALCE_SORT
): OpenerDescalceRow[] {
  return [...rows].sort((a, b) => compareDescalceRows(a, b, sort));
}
